import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Card, CardContent, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useSelector } from 'react-redux';
import { axiosInstance } from '../../../Axios';

const getToken = (loginInfo) => loginInfo?.token || loginInfo?.result?.userToken;
const unwrap = (response) => response?.data?.data || response?.data?.result || response?.data || [];
const asArray = (value) => Array.isArray(value) ? value : [];
const projectIdOf = (row) => row?.projectId ?? row?.ProjectId ?? row?.id ?? '';
const projectNameOf = (row) => row?.projectName ?? row?.ProjectName ?? row?.name ?? 'Unnamed project';
const planIdOf = (row) => row?.projectPlanId ?? row?.ProjectPlanId ?? row?.id ?? '';
const planNameOf = (row) => row?.planTitle ?? row?.PlanTitle ?? row?.title ?? 'Unnamed plan';
const titleOf = (key) => String(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
const displayValue = (value) => value == null || value === '' ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const statusStyle = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('complete') || value.includes('done')) return { color: '#047857', bg: '#d1fae5' };
  if (value.includes('delay') || value.includes('late')) return { color: '#b91c1c', bg: '#fee2e2' };
  if (value.includes('no actual')) return { color: '#a16207', bg: '#fef3c7' };
  return { color: '#1d4ed8', bg: '#dbeafe' };
};

function findRows(value, depth = 0) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object' || depth > 4) return [];
  for (const item of Object.values(value)) {
    if (Array.isArray(item)) return item;
  }
  for (const item of Object.values(value)) {
    const rows = findRows(item, depth + 1);
    if (rows.length) return rows;
  }
  return [];
}

export default function ProjectPlanActualReport() {
  const { loginInfo } = useSelector((state) => state.user);
  const [projects, setProjects] = useState([]);
  const [plans, setPlans] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [projectPlanId, setProjectPlanId] = useState('');
  const [report, setReport] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await axiosInstance.get('/Project/GetProjectList', {
          headers: { Authorization: `Bearer ${getToken(loginInfo)}` },
          params: { Offset: 0, RecordCount: 5000, ProjectName: '' },
        });
        if (active) setProjects(asArray(unwrap(response)));
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load projects.');
      } finally { if (active) setLoadingProjects(false); }
    };
    loadProjects();
    return () => { active = false; };
  }, [loginInfo]);

  useEffect(() => {
    let active = true;
    setPlans([]);
    setProjectPlanId('');
    setReport(null);
    setError('');
    if (!projectId) return undefined;
    const loadPlans = async () => {
      setLoadingPlans(true);
      try {
        const response = await axiosInstance.get('/ProjectPlan/GetProjectPlanList', {
          headers: { Authorization: `Bearer ${getToken(loginInfo)}` },
          params: { ProjectPlanId: 0, ProjectId: projectId, PlanTitle: '', Offset: 0, RecordCount: 5000 },
        });
        if (active) setPlans(asArray(unwrap(response)));
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load project plans.');
      } finally { if (active) setLoadingPlans(false); }
    };
    loadPlans();
    return () => { active = false; };
  }, [loginInfo, projectId]);

  useEffect(() => {
    let active = true;
    setReport(null);
    if (!projectId || !projectPlanId) return undefined;
    const loadReport = async () => {
      setLoadingReport(true);
      setError('');
      try {
        const response = await axiosInstance.get('/Project/GetProjectPlanActualReport', {
          headers: { Authorization: `Bearer ${getToken(loginInfo)}` },
          params: { ProjectId: projectId, ProjectPlanId: projectPlanId },
        });
        if (active) setReport(unwrap(response));
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load the project plan actual report.');
      } finally { if (active) setLoadingReport(false); }
    };
    loadReport();
    return () => { active = false; };
  }, [loginInfo, projectId, projectPlanId]);

  const rows = useMemo(() => {
    const nestedRows = findRows(report);
    if (nestedRows.length) return nestedRows;
    return report && typeof report === 'object' && !Array.isArray(report) ? [report] : asArray(report);
  }, [report]);
  const columns = useMemo(() => {
    const keys = new Set();
    rows.forEach((row) => {
      if (row && typeof row === 'object' && !Array.isArray(row)) Object.keys(row).forEach((key) => keys.add(key));
    });
    return Array.from(keys);
  }, [rows]);
  const summary = useMemo(() => ({
    total: rows.length,
    completed: rows.filter((row) => /complete|done/i.test(String(row?.status || ''))).length,
    waiting: rows.filter((row) => /no actual update/i.test(String(row?.status || ''))).length,
    additional: rows.filter((row) => row?.isAdditionalDay === true).length,
  }), [rows]);
  const selectedProject = projects.find((project) => String(projectIdOf(project)) === String(projectId));
  const selectedPlan = plans.find((plan) => String(planIdOf(plan)) === String(projectPlanId));

  const summaryCards = [
    { label: 'Scheduled activities', value: summary.total, icon: EventAvailableIcon, color: '#2563eb', tint: '#eff6ff' },
    { label: 'Completed', value: summary.completed, icon: PlaylistAddCheckIcon, color: '#059669', tint: '#ecfdf5' },
    { label: 'Awaiting actual update', value: summary.waiting, icon: HourglassEmptyIcon, color: '#d97706', tint: '#fffbeb' },
    { label: 'Additional days', value: summary.additional, icon: FactCheckIcon, color: '#7c3aed', tint: '#f5f3ff' },
  ];

  return (
    <Box sx={{ minHeight: 'calc(100vh - 120px)', p: { xs: 2, md: 3 }, background: '#f1f5f9' }}>
      <Paper elevation={0} sx={{ position: 'relative', overflow: 'hidden', p: { xs: 2.5, md: 3.5 }, mb: 2.5, color: '#fff', borderRadius: 3, background: 'linear-gradient(120deg, #0f172a 0%, #1e3a5f 65%, #075985 100%)', '&::after': { content: '""', position: 'absolute', width: 230, height: 230, border: '1px solid rgba(255,255,255,.12)', borderRadius: '50%', right: -35, top: -105, boxShadow: '0 0 0 32px rgba(255,255,255,.035), 0 0 0 64px rgba(255,255,255,.025)' } }}>
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar variant="rounded" sx={{ width: 52, height: 52, color: '#fde68a', bgcolor: 'rgba(251,191,36,.16)', border: '1px solid rgba(253,230,138,.25)' }}><AssessmentIcon fontSize="large" /></Avatar>
          <Box><Typography variant="overline" sx={{ color: '#93c5fd', letterSpacing: 2, fontWeight: 700 }}>PROJECT CONTROLS / REPORTS</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: 23, md: 30 } }}>Project Plan Actual</Typography>
            <Typography variant="body2" sx={{ mt: .5, color: '#cbd5e1' }}>A clear view of scheduled work, daily updates, and delivery variance.</Typography></Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 2.5, border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: '0 8px 24px rgba(15,23,42,.04)' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#64748b', fontWeight: 800, letterSpacing: .5 }}>REPORT FILTERS</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 260 }} disabled={loadingProjects}>
            <InputLabel id="project-report-label">Project</InputLabel>
            <Select labelId="project-report-label" label="Project" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              {projects.map((project) => <MenuItem key={projectIdOf(project)} value={projectIdOf(project)}>{projectNameOf(project)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 260 }} disabled={!projectId || loadingPlans}>
            <InputLabel id="plan-report-label">Project Plan</InputLabel>
            <Select labelId="plan-report-label" label="Project Plan" value={projectPlanId} onChange={(event) => setProjectPlanId(event.target.value)}>
              {plans.map((plan) => <MenuItem key={planIdOf(plan)} value={planIdOf(plan)}>{planNameOf(plan)}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {(loadingProjects || loadingPlans || loadingReport) && <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>}
      {!loadingProjects && !loadingPlans && !loadingReport && !error && !projectId && <Alert severity="info">Select a project to begin.</Alert>}
      {!loadingPlans && !loadingReport && !error && projectId && !plans.length && <Alert severity="info">No project plans are available for this project.</Alert>}
      {!loadingReport && !error && projectId && projectPlanId && report != null && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, mb: 2.5 }}>
            {summaryCards.map(({ label, value, icon: Icon, color, tint }) => (
              <Card key={label} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, boxShadow: '0 8px 20px rgba(15,23,42,.035)' }}>
                <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box><Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>{label}</Typography><Typography variant="h4" sx={{ mt: .5, color: '#0f172a', fontWeight: 800 }}>{value}</Typography></Box>
                  <Avatar sx={{ width: 44, height: 44, color, bgcolor: tint }}><Icon /></Avatar>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Paper elevation={0} sx={{ mb: 2, px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', border: '1px solid #dbeafe', borderRadius: 2.5, bgcolor: '#f8fbff' }}>
            <Box><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: .5 }}>REPORTING ON</Typography>
              <Typography variant="subtitle1" sx={{ color: '#0f172a', fontWeight: 800 }}>{projectNameOf(selectedProject)} <Box component="span" sx={{ color: '#94a3b8', mx: .5 }}>/</Box> {planNameOf(selectedPlan)}</Typography></Box>
            <Chip label={`${summary.total} ${summary.total === 1 ? 'activity' : 'activities'}`} size="small" sx={{ color: '#1d4ed8', bgcolor: '#dbeafe', fontWeight: 800 }} />
          </Paper>

        <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #e2e8f0', borderRadius: 2.5, boxShadow: '0 10px 30px rgba(15,23,42,.045)' }}>
          <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Box><Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a' }}>Activity details</Typography><Typography variant="caption" color="text.secondary">Planned schedule compared with actual site updates</Typography></Box>
          </Box>
          {rows.length > 0 && columns.length > 0 ? (
            <TableContainer sx={{ maxHeight: '65vh' }}>
              <Table size="medium" stickyHeader>
                <TableHead><TableRow>{['scheduleDayNo', 'activity', 'plannedDate', 'actualDateDone', 'differenceDays', 'status', 'dailyUpdate'].map((column) => <TableCell key={column} sx={{ py: 1.5, color: '#64748b', fontSize: 11, letterSpacing: .7, fontWeight: 800, textTransform: 'uppercase', bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>{({ scheduleDayNo: 'Plan day', activity: 'Planned activity', plannedDate: 'Planned date', actualDateDone: 'Actual date', differenceDays: 'Variance', dailyUpdate: 'Site update' })[column]}</TableCell>)}</TableRow></TableHead>
                <TableBody>{rows.map((row, index) => {
                  const colors = statusStyle(row?.status);
                  return <TableRow key={row?.planDayId ?? index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip size="small" label={`Day ${row?.scheduleDayNo ?? row?.originalDayNo ?? index + 1}`} sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 800 }} />{row?.isAdditionalDay && <Typography variant="caption" display="block" sx={{ mt: .5, color: '#7c3aed', fontWeight: 700 }}>Additional</Typography>}</TableCell>
                    <TableCell sx={{ minWidth: 210 }}><Typography variant="body2" sx={{ fontWeight: 750, color: '#0f172a' }}>{displayValue(row?.activity)}</Typography>{row?.site && <Typography variant="caption" color="text.secondary">{row.site}</Typography>}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155', fontWeight: 600 }}>{formatDate(row?.plannedDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: row?.actualDateDone ? '#047857' : '#94a3b8', fontWeight: 600 }}>{formatDate(row?.actualDateDone)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row?.differenceDays == null ? <Typography variant="body2" color="text.disabled">—</Typography> : <Chip size="small" label={`${Number(row.differenceDays) > 0 ? '+' : ''}${row.differenceDays} days`} color={Number(row.differenceDays) > 0 ? 'error' : Number(row.differenceDays) < 0 ? 'success' : 'default'} variant="outlined" />}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip size="small" label={displayValue(row?.status)} sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 800 }} /></TableCell>
                    <TableCell sx={{ minWidth: 180, maxWidth: 300, color: row?.dailyUpdate ? '#334155' : '#94a3b8' }}>{displayValue(row?.dailyUpdate)}</TableCell>
                  </TableRow>;
                })}</TableBody>
              </Table>
            </TableContainer>
          ) : <Box sx={{ p: 5, textAlign: 'center' }}><Typography variant="subtitle1" fontWeight={700}>No activities found</Typography><Typography variant="body2" color="text.secondary">There are no planned activities in this report.</Typography></Box>}
        </Paper>
        </>
      )}
    </Box>
  );
}
